<template>
  <div>
    <br />
    <h1 class="text-xs-center">Advanced: Payout Settings</h1>
    <v-layout row wrap justify-center>
      <v-flex xs12 sm10 md8 lg6 pa-2>
        <v-card>
          <v-card-title>
            <span class="headline">Global Payout Settings</span>
          </v-card-title>
          <v-card-text>
            <div v-if="!currentSettings" class="text-xs-center">
              <p>No payout settings were found for this agency.</p>
              <v-btn color="primary" :disabled="apiInProgress" @click="loadSettings()">Reload</v-btn>
            </div>
            <div v-else>
              <v-layout row wrap>
                <v-flex xs12 pa-1>
                  <v-select
                    v-model="currentSettings.agencyId"
                    :items="payoutAgencyOptions"
                    item-text="text"
                    item-value="value"
                    label="Agency ID"
                    disabled
                  />
                </v-flex>
                <v-flex xs12 sm6 pa-1>
                  <div class="layout align-start mb-1">
                    <v-tooltip top max-width="360">
                      <template v-slot:activator="{ on }">
                        <v-btn icon small class="ma-0 ml-1" v-on="on">
                          <v-icon small>fa fa-info-circle</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ payoutFieldTooltips.smarttransytFee }}</span>
                    </v-tooltip>
                    <currency-field
                      v-model.number="currentSettings.smarttransytFee"
                      :child-props="{ label: payoutFieldLabels.smarttransytFee, min: 0, placeholder: '0.00' }"
                    />
                  </div>
                </v-flex>
                <v-flex xs12 sm6 pa-1>
                  <div class="layout align-start mb-1">
                    <v-tooltip top max-width="360">
                      <template v-slot:activator="{ on }">
                        <v-btn icon small class="ma-0 ml-1" v-on="on">
                          <v-icon small>fa fa-info-circle</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ payoutFieldTooltips.smarttransytFeePercentage }}</span>
                    </v-tooltip>
                    <v-text-field
                      v-model.number="currentSettings.smarttransytFeePercentage"
                      :label="payoutFieldLabels.smarttransytFeePercentage"
                      type="number"
                      min="0"
                      step="1"
                      suffix="%"
                    />
                  </div>
                </v-flex>
                <v-flex xs12 sm6 pa-1>
                  <div class="layout align-start mb-1">
                    <v-tooltip top max-width="360">
                      <template v-slot:activator="{ on }">
                        <v-btn icon small class="ma-0 ml-1" v-on="on">
                          <v-icon small>fa fa-info-circle</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ payoutFieldTooltips.beaconFee }}</span>
                    </v-tooltip>
                    <currency-field
                      v-model.number="currentSettings.beaconFee"
                      :child-props="{ label: payoutFieldLabels.beaconFee, min: 0, placeholder: '0.00' }"
                    />
                  </div>
                </v-flex>
                <v-flex xs12 sm6 pa-1>
                  <div class="layout align-start mb-1">
                    <v-tooltip top max-width="360">
                      <template v-slot:activator="{ on }">
                        <v-btn icon small class="ma-0 ml-1" v-on="on">
                          <v-icon small>fa fa-info-circle</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ payoutFieldTooltips.beaconMaintenanceFee }}</span>
                    </v-tooltip>
                    <currency-field
                      v-model.number="currentSettings.beaconMaintenanceFee"
                      :child-props="{ label: payoutFieldLabels.beaconMaintenanceFee, min: 0, placeholder: '0.00' }"
                    />
                  </div>
                </v-flex>
                <v-flex xs12 sm6 pa-1>
                  <div class="layout align-start mb-1">
                    <v-tooltip top max-width="360">
                      <template v-slot:activator="{ on }">
                        <v-btn icon small class="ma-0 ml-1" v-on="on">
                          <v-icon small>fa fa-info-circle</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ payoutFieldTooltips.perDayDriverFee }}</span>
                    </v-tooltip>
                    <currency-field
                      v-model.number="currentSettings.perDayDriverFee"
                      :child-props="{ label: payoutFieldLabels.perDayDriverFee, min: 0, placeholder: '0.00' }"
                    />
                  </div>
                </v-flex>
                <v-flex xs12 sm6 pa-1>
                  <div class="layout align-start mb-1">
                    <v-tooltip top max-width="360">
                      <template v-slot:activator="{ on }">
                        <v-btn icon small class="ma-0 ml-1" v-on="on">
                          <v-icon small>fa fa-info-circle</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ payoutFieldTooltips.perKwHFee }}</span>
                    </v-tooltip>
                    <currency-field
                      v-model.number="currentSettings.perKwHFee"
                      :child-props="{ label: payoutFieldLabels.perKwHFee, min: 0, placeholder: '0.00' }"
                    />
                  </div>
                </v-flex>
                <v-flex xs12 pa-1 v-if="currentSettings.dateUpdatedLabel">
                  Date updated: {{ currentSettings.dateUpdatedLabel }}
                </v-flex>
              </v-layout>
            </div>
          </v-card-text>
          <v-divider />
          <v-card-actions>
            <v-btn color="warning" flat :disabled="apiInProgress || !isCurrentSettingsEdited" @click="cancelEdit()">
              Cancel
            </v-btn>
            <v-spacer />
            <v-btn :disabled="apiInProgress" @click="loadSettings()">Reload</v-btn>
            <v-btn color="primary" :disabled="apiInProgress || !isCurrentSettingsEdited || !currentSettings" :loading="apiInProgress" @click="saveSettings()">
              Save
            </v-btn>
          </v-card-actions>
        </v-card>
        <v-card class="mt-3">
          <v-card-title>
            <span class="headline">Mobile Money Settings</span>
          </v-card-title>
          <v-card-text>
            <span class="subheading">ITC Uniwallet</span>
            <p class="caption">Provision the public HTTPS callback URL ITC Uniwallet should POST transaction results to. ITC sends the same callback shape for both debit (collections) and credit (disbursement), so both product flows should point to the dedicated ITC endpoint below. The handler routes by the <code>network</code> field in the callback (MTN, ARTLTIGO, VODAFONE).</p>
            <p class="caption"><strong>Dedicated ITC callback endpoint:</strong> <code>https://&lt;your-public-host&gt;/v1/mobile-money-account/mobile-money/completed/itc/</code></p>
            <v-layout row wrap>
              <v-flex xs12 sm6 pa-1>
                <v-text-field
                  v-model="itcCollectionsCallbackUrl"
                  label="Collections callback URL"
                  placeholder="https://example.ngrok.app/v1/mobile-money-account/mobile-money/completed/itc/"
                  :disabled="itcProvisionInProgress"
                />
              </v-flex>
              <v-flex xs12 sm6 pa-1 class="layout row wrap align-center">
                <v-btn
                  color="primary"
                  :disabled="itcProvisionInProgress || !itcCollectionsCallbackUrl"
                  :loading="itcProvisionInProgressCollections"
                  @click="provisionItcCallbackUrl('collections')"
                >
                  Provision
                </v-btn>
              </v-flex>
              <v-flex xs12 sm6 pa-1>
                <v-text-field
                  v-model="itcDisbursementCallbackUrl"
                  label="Disbursement callback URL"
                  placeholder="https://example.ngrok.app/v1/mobile-money-account/mobile-money/completed/itc/"
                  :disabled="itcProvisionInProgress"
                />
              </v-flex>
              <v-flex xs12 sm6 pa-1 class="layout row wrap align-center">
                <v-btn
                  color="primary"
                  :disabled="itcProvisionInProgress || !itcDisbursementCallbackUrl"
                  :loading="itcProvisionInProgressDisbursement"
                  @click="provisionItcCallbackUrl('disbursement')"
                >
                  Provision
                </v-btn>
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import src from './src'
export default src
</script>
